import React, { useEffect, useRef, useState } from "react";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import { HiChevronDown, HiChevronUp, HiMagnifyingGlass, HiPlus } from "react-icons/hi2";
import { classNames } from "../utils";

export default ({ id, options, values, onSelectedChange, placeholder = "Select an option", onCreateNew, buttonClassName = "px-2 pr-1 py-2", onOpen }) => {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);
  const refInput = useRef(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (isOpen) refInput.current.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!values) return;
    setSelectedOptions(values);
  }, [values]);

  function handleOptionClick(option) {
    let _selectedOptions;
    if (selectedOptions.find((_option) => _option.value === option.value)) {
      _selectedOptions = selectedOptions.filter((_option) => _option.value !== option.value);
    } else {
      _selectedOptions = [...selectedOptions, option];
    }
    setSelectedOptions(_selectedOptions);
    onSelectedChange(_selectedOptions);
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      const idRegex = new RegExp(id);
      if (ref.current && !ref.current.contains(event.target) && !idRegex.test(event.target.id)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) setSearch("");
  }, [isOpen]);

  function handleCreateNew() {
    if (onCreateNew) {
      const newOption = { value: search.toLowerCase().replace(/\s+/g, "-"), label: search };
      onCreateNew(newOption);
      setSearch("");
    }
  }

  return (
    <div className="relative">
      <button
        id={id}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (onOpen) onOpen();
          setIsOpen(!isOpen);
        }}
        className={`inline-flex w-full justify-between items-center gap-4 border border-gray-200 text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${buttonClassName}`}>
        <span id={`${id}-text`} className="flex-1 text-left">
          {selectedOptions.length === 0 ? placeholder : `${placeholder} (${selectedOptions.length})`}
        </span>
        {isOpen ? <HiChevronUp className="h-4 w-4" /> : <HiChevronDown className="h-4 w-4" />}
      </button>
      {isOpen && (
        <div ref={ref} className="absolute z-20 mt-1 max-h-72 w-full bg-white shadow-lg rounded-md overflow-y-scroll">
          <ul className="border border-gray-200 divide-y divide-gray-200 list-none w-full">
            <li className={classNames(`flex items-center gap-1 text-sm cursor-pointer w-full`)}>
              <div className="relative w-full py-2">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <HiMagnifyingGlass className="h-4 w-4 text-gray-400" aria-hidden="true" />
                </div>
                <input
                  ref={refInput}
                  className="input block w-full rounded-md border-gray-300 pl-10 focus:border-blue-500 focus:ring-blue-500 text-sm"
                  placeholder={onCreateNew ? `Search or create new` : `Search`}
                  value={search}
                  onChange={(e) => {
                    e.persist();
                    setSearch(e.target.value);
                  }}
                />
              </div>
            </li>
            {options
              .filter((o) => {
                if (!search) return true;
                return o.label.toLowerCase().includes(search.toLowerCase());
              })
              .map((option) => {
                const isSelected = selectedOptions.find((_option) => _option.value === option.value);
                return (
                  <li
                    key={option.value}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOptionClick(option);
                    }}
                    className={classNames(`flex items-center gap-1 hover:bg-gray-100 text-sm px-2 py-1.5 cursor-pointer w-full`)}>
                    {isSelected ? <MdCheckBox className="text-blue-500 h-4 w-4" /> : <MdCheckBoxOutlineBlank className="text-gray-500 h-4 w-4" />}
                    <span className={classNames("flex-1", isSelected ? "font-semibold" : "font-normal")}>{option.label}</span>
                  </li>
                );
              })}
            {onCreateNew && search && !options.find((o) => o.label.toLowerCase() === search.toLowerCase()) && (
              <li
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreateNew();
                }}
                className={classNames(`flex items-center gap-1 hover:bg-gray-100 text-sm px-2 py-1.5 cursor-pointer w-full`)}>
                <HiPlus className="text-gray-500 h-4 w-4" />
                <span className="flex-1 font-semibold">Create "{search}"</span>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};
